import * as React from 'react';
import * as classnames from 'classnames';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { ResponsiveModal } from '@estee/elc-responsive-modal';
import { ProductNotifyMeButton } from '~views/product-notify-me/ProductNotifyMeButton';
import {
    IWaitlist,
    ProductNotifyMePopUpContent
} from '~views/product-notify-me//ProductNotifyMePopUpContent';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/CommunicationsService';
import { RemoveIcon } from '@estee/elc-icons';
import { ProductNotifyMeViewController } from '~controllers/ProductNotifyMeViewController';
import { IProductNotifyMeConfig } from '~domain/entities/Product';
import { addConfig, addContext, configConsumer, translate } from '@estee/elc-service';
import { getProductNotifyMePopupUnderlayStyle } from '~domain/services/ProductNotifyMeService';
import { observable } from 'mobx';

export enum Translations {
    notifyMeLabel,
    notifyMeConfirmationTitle,
    notifyMePopUpHeaderText,
    notifyMeConfirmationText
}

export type ITranslations = { [key in keyof typeof Translations]: string };

export interface ICompliance {
    gdpr: boolean;
}

export interface IProductNotifyMePopUpConfig extends IProductNotifyMeConfig {
    zIndex?: number;
    compliance?: ICompliance;
    subscriptionType: string;
    customNotifyMeLabel?: string;
    ctaType?: string;
    waitlist?: IWaitlist;
    skuId: string;
    skuBaseId: string;
}

export interface IProductNotifyMePopUp {
    config: IProductNotifyMePopUpConfig;
    translations?: ITranslations;
}

const Wrapper = styled.div`
    display: inline-block;
    width: 100%;
`;

const ProductNotifyMeModal = styled(ResponsiveModal)`
    padding: 0;
`;

const ProductNotifyMePopUpCloseButton = styled.button`
    cursor: pointer;
    float: right;
    border: none;
    outline: none;
    margin: 10px;
`;

const productNotifyMePopUpWrapperClassNames = classnames(
    'elc-product-notify-me-pop-up-wrapper',
    'js-product-notify-me-pop-up-wrapper'
);

const productNotifyMePopUpCloseButtonClassNames = classnames(
    'elc-product-notify-me-pop-up-close-button',
    'js-product-notify-me-pop-up-close-button'
);

const productNotifyMePopUpCloseButtonIconClassNames = classnames(
    'elc-product-notify-me-pop-up-close-button-icon',
    'js-product-notify-me-pop-up-close-button-icon'
);
@addContext()
@addConfig(['waitlist'])
@configConsumer(['waitlist'])
@translate([
    'notifyMeLabel',
    'notifyMeConfirmationTitle',
    'notifyMePopUpHeaderText',
    'notifyMeConfirmationText'
])
@observer
class ProductNotifyMePopUp extends React.Component<IProductNotifyMePopUp> {
    @observable private productNotifyMeViewController: ProductNotifyMeViewController;

    constructor(props: IProductNotifyMePopUp) {
        super(props);
        this.productNotifyMeViewController = diContainer.get(
            serviceNames.productNotifyMeViewController
        );
        this.productNotifyMeViewController.initialize(props.config);
    }

    public async componentDidMount() {
        await this.productNotifyMeViewController.loaded(this.props);
    }

    private getNotifyMePopUpHeaderText = (productName: string) => {
        const { translations } = this.props;

        const { notifyMePopUpHeaderText } = translations as ITranslations;

        return notifyMePopUpHeaderText.replace('::productname::', productName);
    };

    private getNotifyMeConfirmationText = (productName: string) => {
        const { translations } = this.props;

        const { notifyMeConfirmationText } = translations as ITranslations;

        return notifyMeConfirmationText.replace('::productname::', productName);
    };

    public render() {
        const content = this.getNotifyMePopUpHeaderText(this.props.config.productDisplayName);

        const confirmationContent = this.getNotifyMeConfirmationText(
            this.props.config.productDisplayName
        );

        const { translations } = this.props;

        const { notifyMeLabel, notifyMeConfirmationTitle } = translations as ITranslations;
        const confirmationContentTitle = notifyMeConfirmationTitle;
        const {
            data,
            onCloseButtonClick,
            onSubmitButtonClick,
            onNotifyMeClick,
            onCloseNotifyMePopUp,
            setAcceptsTerms,
            toggleGdprPopupOpen
        } = this.productNotifyMeViewController;
        const {
            notifyMeForm,
            isProductNotifyMeConfirmationOpen,
            isPopUpOpen,
            acceptsTerms,
            isGdprPopupOpen,
            gdprConfirmation
        } = data;
        const { zIndex, productDisplayName, customNotifyMeLabel, ctaType } = this.props.config;
        const notifyMeButtonLabel = customNotifyMeLabel || notifyMeLabel;

        return (
            <Wrapper>
                <ProductNotifyMeButton
                    label={notifyMeButtonLabel}
                    ctaType={ctaType}
                    onNotifyMeClick={onNotifyMeClick}
                />
                <ProductNotifyMeModal
                    className={productNotifyMePopUpWrapperClassNames}
                    title={`${productDisplayName}`}
                    isOpen={isPopUpOpen}
                    deactivateModal={onCloseNotifyMePopUp}
                    underlayStyle={getProductNotifyMePopupUnderlayStyle(zIndex, 0)}
                    onMaskClick={onCloseNotifyMePopUp}
                >
                    <ProductNotifyMePopUpCloseButton
                        className={productNotifyMePopUpCloseButtonClassNames}
                        onClick={onCloseNotifyMePopUp}
                    >
                        <RemoveIcon className={productNotifyMePopUpCloseButtonIconClassNames} />
                    </ProductNotifyMePopUpCloseButton>
                    <ProductNotifyMePopUpContent
                        content={content}
                        notifyMeForm={notifyMeForm}
                        productDisplayName={productDisplayName}
                        onSubmitButtonClick={onSubmitButtonClick}
                        confirmationContent={confirmationContent}
                        confirmationContentTitle={confirmationContentTitle}
                        onCloseButtonClick={onCloseButtonClick}
                        isProductNotifyMeConfirmationOpen={isProductNotifyMeConfirmationOpen}
                        onTermsCheckedChange={setAcceptsTerms}
                        acceptsTerms={acceptsTerms}
                        zIndex={zIndex}
                        onNotifyMeNoticeInfoClick={toggleGdprPopupOpen}
                        isGdprPopupOpen={isGdprPopupOpen}
                        gdprConfirmation={gdprConfirmation}
                    />
                </ProductNotifyMeModal>
            </Wrapper>
        );
    }
}

export default ProductNotifyMePopUp;
